/** Credit balance, packs, and checkout hooks */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient, ApiError } from '../lib/api-client'

export type CreditBalance = {
  purchasedCredits: number
  freeCredits: number
  totalCredits: number
  freeCreditsLastResetAt?: number | null
}

export type CreditPack = {
  id: string
  name: string
  credits: number
  bonusCredits: number
  priceCents: number
  sortOrder: number
}

export type CheckoutResponse = {
  checkoutUrl: string
}

export type PaymentsConfig = {
  environment: 'sandbox' | 'production'
  providerConfigured: boolean
  webhookConfigured: boolean
  routeCosts: Array<{
    key: string
    label: string
    method: string
    pathPrefix: string
    cost: number
  }>
  packProductCoverage: {
    active: number
    configured: number
    missingProductIds: string[]
    packs: Array<{ id: string; name: string; polarProductId: string | null }>
  }
}

export type CreditTransaction = {
  id: string
  tenantId: string
  userId: string
  type: string
  amount: number
  balanceAfter: number
  description: string | null
  metadata?: Record<string, unknown> | null
  createdAt: number
}

export type CreditPurchase = {
  id: string
  tenantId: string
  userId: string
  packId: string | null
  provider: string
  externalId: string
  packName: string
  creditsGranted: number
  amountPaid: number
  currency: string
  status: string
  createdAt: number
}

type Paginated<T> = {
  data: T[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export type PaginatedCredits<T> = Paginated<T>

export type CreditLedgerQuery = {
  type?: string
  limit?: number
  offset?: number
}

export function usePaymentsConfig() {
  return useQuery<PaymentsConfig, ApiError>({
    queryKey: ['payments', 'config'],
    queryFn: () => apiClient.get<PaymentsConfig>('/api/payments/config'),
    staleTime: 1000 * 60 * 60,
  })
}

export function useCreditBalance() {
  return useQuery<CreditBalance, ApiError>({
    queryKey: ['credits', 'balance'],
    queryFn: () => apiClient.get<CreditBalance>('/api/credits/balance'),
    staleTime: 1000 * 30,
  })
}

export function useCreditPacks() {
  return useQuery<CreditPack[], ApiError>({
    queryKey: ['credits', 'packs'],
    queryFn: () => apiClient.get<CreditPack[]>('/api/credits/packs'),
    staleTime: 1000 * 60 * 10,
  })
}

export function useCreditTransactions(query: CreditLedgerQuery = {}) {
  const { type, limit, offset } = query
  return useQuery<Paginated<CreditTransaction>, ApiError>({
    queryKey: ['credits', 'transactions', type ?? 'all', limit ?? 'default', offset ?? 0],
    queryFn: () => {
      const params = new URLSearchParams()
      if (type) params.set('type', type)
      if (limit) params.set('limit', String(limit))
      if (offset) params.set('offset', String(offset))
      const suffix = params.toString() ? `?${params}` : ''
      return apiClient.get<Paginated<CreditTransaction>>(`/api/credits/transactions${suffix}`)
    },
    staleTime: 1000 * 30,
  })
}

export function useCreditPurchases(query: Omit<CreditLedgerQuery, 'type'> = {}) {
  const { limit, offset } = query
  return useQuery<Paginated<CreditPurchase>, ApiError>({
    queryKey: ['credits', 'purchases', limit ?? 'default', offset ?? 0],
    queryFn: () => {
      const params = new URLSearchParams()
      if (limit) params.set('limit', String(limit))
      if (offset) params.set('offset', String(offset))
      const suffix = params.toString() ? `?${params}` : ''
      return apiClient.get<Paginated<CreditPurchase>>(`/api/credits/purchases${suffix}`)
    },
    staleTime: 1000 * 30,
  })
}

export function useCreateCheckout() {
  const qc = useQueryClient()
  return useMutation<CheckoutResponse, ApiError, { packId: string }>({
    mutationFn: (input) => apiClient.post<CheckoutResponse>('/api/payments/checkout', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credits', 'balance'] })
    },
  })
}
